// SEO utility functions for structured data and metadata
import { NEXT_PUBLIC_FRONTEND_URL } from '@/shared/envs';

const BASE_URL = NEXT_PUBLIC_FRONTEND_URL || 'https://darpiano.com';

export interface StructuredDataWebsite {
  '@context': 'https://schema.org';
  '@type': 'WebSite';
  name: string;
  url: string;
  description: string;
  potentialAction: {
    '@type': 'SearchAction';
    target: string;
    'query-input': string;
  };
}

export interface StructuredDataMusicComposition {
  '@context': 'https://schema.org';
  '@type': 'MusicComposition';
  name: string;
  composer: {
    '@type': 'Person';
    name: string;
  };
  description?: string;
  url: string;
  datePublished?: string;
  dateModified?: string;
  inLanguage: string[];
}

export function generateWebsiteStructuredData(): StructuredDataWebsite {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'DarPiano',
    url: BASE_URL,
    description:
      'Learn piano with interactive sheet music and MIDI connectivity. Practice classical pieces with real-time feedback.',
    potentialAction: {
      '@type': 'SearchAction',
      target: `${BASE_URL}/posts?search={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };
}

export function generateMusicCompositionStructuredData(
  title: string,
  composer: string,
  slug: string,
  description?: string,
  datePublished?: string,
  dateModified?: string
): StructuredDataMusicComposition {
  return {
    '@context': 'https://schema.org',
    '@type': 'MusicComposition',
    name: title,
    composer: {
      '@type': 'Person',
      name: composer,
    },
    description,
    url: `${BASE_URL}/posts/${slug}`,
    datePublished,
    dateModified,
    inLanguage: ['en', 'ru'],
  };
}

export function generateBreadcrumbStructuredData(
  items: Array<{ name: string; url: string }>
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url.startsWith('http') ? item.url : `${BASE_URL}${item.url}`,
    })),
  };
}

// Utility function to generate hreflang links
export function generateHreflangLinks(
  currentPath: string,
  languages: string[] = ['en', 'ru']
) {
  return languages.map((lang) => ({
    rel: 'alternate',
    hrefLang: lang,
    href: `${BASE_URL}${currentPath}?lang=${lang}`,
  }));
}

// Generate canonical URL
export function getCanonicalUrl(path: string): string {
  if (typeof window !== 'undefined') {
    // Client side
    const baseUrl = window.location.origin;
    if (path === '/') return baseUrl;
    return `${baseUrl}${path}`;
  }

  // Server side
  const serverUrl = NEXT_PUBLIC_FRONTEND_URL ||
    (process.env.NODE_ENV === 'development' ? 'http://127.0.0.1:3001' : null);

  if (!serverUrl) {
    // During `next build`, static pages (e.g. /_not-found) are pre-rendered
    // without runtime env vars. Use production fallback for build-time SEO.
    // At actual request time, NEXT_PUBLIC_FRONTEND_URL MUST be set via k8s.
    if (process.env.NEXT_PHASE === 'phase-production-build') {
      return path === '/' ? 'https://darpiano.com' : `https://darpiano.com${path}`;
    }
    throw new Error("NEXT_PUBLIC_FRONTEND_URL is required in production");
  }

  if (path === '/') return serverUrl;
  return `${serverUrl}${path}`;
}
